/** 检测当前环境是否支持 */
const envImageAbilityCheckArr: string[] = ['format'];
const envVideoAbilityCheckArr: string[] = [];

type ImageHandlePriority = {
  format: number;
  quality: number;
  resize: number;
  watermark: number;
  rotate: number;
  'auto-orient': number;
  'average-hue': number;
  blur: number;
  bright: number;
  circle: number;
  contrast: number;
  indexCrop: number;
  info: number;
  interlace: number;
  'rounded-corners': number;
  sharpen: number;
};

type VideoHandlePriority = {
  snapshot: number;
};

/** 图片处理优先级，参数越大，优先处理级别越高 */
const imageHandlePriority: ImageHandlePriority = {
  format: 900,
  quality: 900,
  resize: 1000,
  watermark: 800,
  rotate: 1000,
  'auto-orient': 1000,
  'average-hue': 1000,
  blur: 1000,
  bright: 1000,
  circle: 1000,
  contrast: 1000,
  indexCrop: 1000,
  info: 1000,
  interlace: 1000,
  'rounded-corners': 1000,
  sharpen: 1000,
};

const videoHandlePriority: VideoHandlePriority = {
  snapshot: 1000,
};

function toString(obj: unknown): string {
  return Object.prototype.toString.call(obj);
}

let supportWebp = true;
let isSupportWebpChecked = false;

function isSupportWebp(): boolean {
  let _a;
  if (
    !((_a = globalThis === null || globalThis === void 0 ? void 0 : globalThis.window) === null ||
    _a === void 0
      ? void 0
      : _a.document)
  ) {
    //兼容ssr,node端默认全部支持
    return true;
  }
  if (isSupportWebpChecked) {
    return supportWebp;
  }
  try {
    const support =
      window.document
        .createElement('canvas')
        .toDataURL('image/webp', 0.5)
        .indexOf('data:image/webp') === 0;
    supportWebp = support;
    isSupportWebpChecked = true;
    return support;
  } catch (err) {
    supportWebp = false;
    isSupportWebpChecked = true;
    return false;
  }
}

export type ResourceOption = {
  [key: string]: unknown;
};

/**
 *
 * @param formatType
 * @param resourceTypes
 * @param supportAllFeature 是否支持所有特性。例如在ssr场景，server端也支持
 *
 * @returns
 */
function getSupportResourceType(
  formatType: string,
  resourceTypes: string[],
  supportAllFeature?: boolean
): string | undefined {
  if (!resourceTypes.length) {
    return undefined;
  }
  if (formatType === 'format') {
    return resourceTypes.find(item => {
      if (supportAllFeature) {
        return true;
      }
      if (item === 'webp') {
        return isSupportWebp();
      }
      return true;
    });
  }
  return resourceTypes[0];
}

/**
 *
 * @param url 传入的url
 * @param resourceType 资源类型
 * @param option 对应资源的配置项
 * @param supportAllFeature 是否不用交验当前环境是否支持所有特性
 * @returns
 */
function transResource(
  url: string | undefined,
  resourceType: 'image' | 'video',
  option: ResourceOption,
  supportAllFeature?: boolean
): string | undefined {
  if (!url || !option) {
    return url;
  }
  const priority = resourceType === 'image' ? imageHandlePriority : videoHandlePriority;
  const keys = Object.keys(option).sort((a, b) => {
    return (
      (priority[a as keyof typeof priority] || 0) - (priority[b as keyof typeof priority] || 0)
    );
  });
  if (!keys.length) {
    return url;
  }
  const formats: string[] = [];
  const envAbilityCheckArr =
    resourceType === 'image' ? envImageAbilityCheckArr : envVideoAbilityCheckArr;
  keys.forEach(item => {
    let attribute = option[item];
    let str = `${String(item)}`;
    if (envAbilityCheckArr.includes(str)) {
      //需要检测当前特性，当前环境是否支持。需要时数组，方便统一处理。当前环境不支持时候，降级
      attribute = attribute instanceof Array ? attribute : [attribute];
    }
    if (attribute) {
      if (toString(attribute) === '[object Object]') {
        Object.keys(attribute as object).forEach(attr => {
          str += `,${attr}_${(attribute as Record<string, unknown>)[attr]}`;
        });
        formats.push(str);
      } else if (attribute instanceof Array) {
        if (item === 'format' && attribute.length) {
          const supportType = getSupportResourceType(
            item,
            attribute as string[],
            supportAllFeature
          );
          if (supportType) {
            formats.push(str + `,${supportType}`);
          } else {
            return;
          }
        }
      } else if (toString(attribute) === '[object Boolean]') {
        formats.push(str);
      } else {
        formats.push(str + `,${attribute}`);
      }
    }
  });
  if (formats.length === 0) {
    return url;
  }
  return `${url}?x-oss-process=${resourceType}/${formats.join('/')}`;
}

function transImageResource(
  url: string | undefined,
  option: ResourceOption,
  supportAllFeature?: boolean
): string | undefined {
  return transResource(url, 'image', option, supportAllFeature);
}

export { transImageResource };
